import React, { useState, useEffect } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import { SyncStatus } from "../hooks/useAuthSync";

// Pulsing animation for the icon
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.85; }
`;

// Subtle shimmer effect for loading state
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Container = styled(Box)({
  flex: 1,
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)",
  padding: "24px",
});

const IconContainer = styled(Box)({
  width: 80,
  height: 80,
  borderRadius: "50%",
  background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 24,
  animation: `${pulse} 2s ease-in-out infinite`,
  boxShadow: "0 8px 32px rgba(99, 102, 241, 0.25)",
});

const ProgressBar = styled(Box)({
  width: "100%",
  maxWidth: 200,
  height: 4,
  borderRadius: 2,
  backgroundColor: "#E5E7EB",
  marginTop: 24,
  overflow: "hidden",
  position: "relative",
});

const ProgressFill = styled(Box)(({ width }) => ({
  height: "100%",
  borderRadius: 2,
  background: "linear-gradient(90deg, #6366F1, #8B5CF6, #6366F1)",
  backgroundSize: "200% 100%",
  animation: `${shimmer} 1.5s linear infinite`,
  transition: "width 0.5s ease-out",
  width: `${width}%`,
}));

// Messages to show during different sync stages
const MESSAGES = {
  connecting: {
    newUser: [
      "Connecting securely...",
      "Setting things up...",
      "Almost there...",
    ],
    returningUser: [
      "Connecting securely...",
      "Verifying your account...",
      "Almost there...",
    ],
  },
  syncing: {
    newUser: [
      "Creating your account...",
      "Getting everything ready...",
      "Preparing your experience...",
    ],
    returningUser: [
      "Signing you in...",
      "Loading your profile...",
      "Getting things ready...",
    ],
  },
};

const AuthLoadingScreen = ({
  syncStatus = SyncStatus.IDLE,
  isNewUser = false,
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  // Rotate through messages every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % 3);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Animate progress bar based on sync status
  useEffect(() => {
    const targetProgress =
      syncStatus === SyncStatus.CONNECTING
        ? 30
        : syncStatus === SyncStatus.SYNCING
          ? 70
          : syncStatus === SyncStatus.SUCCESS
            ? 100
            : 10;

    // Animate to target
    const animateProgress = () => {
      setProgress((prev) => {
        if (prev < targetProgress) {
          return Math.min(prev + 2, targetProgress);
        }
        return prev;
      });
    };

    const interval = setInterval(animateProgress, 100);
    return () => clearInterval(interval);
  }, [syncStatus]);

  const userType = isNewUser ? "newUser" : "returningUser";
  const messages =
    syncStatus === SyncStatus.SYNCING
      ? MESSAGES.syncing[userType]
      : MESSAGES.connecting[userType];
  const currentMessage = messages[messageIndex] || messages[0];

  return (
    <Container>
      <IconContainer>
        {/* Simple checkmark/user icon */}
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="8" r="4" fill="white" />
          <path
            d="M12 14c-4 0-7 2-7 4.5V20h14v-1.5c0-2.5-3-4.5-7-4.5z"
            fill="white"
          />
        </svg>
      </IconContainer>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: "#1F2937",
          textAlign: "center",
          mb: 1,
        }}
      >
        Welcome!
      </Typography>

      <Typography
        sx={{
          color: "#6B7280",
          textAlign: "center",
          fontSize: "0.95rem",
          minHeight: 24,
          transition: "opacity 0.3s ease",
        }}
      >
        {currentMessage}
      </Typography>

      <ProgressBar>
        <ProgressFill width={progress} />
      </ProgressBar>
    </Container>
  );
};

export default AuthLoadingScreen;
